import { RowSelectionState } from '@tanstack/react-table';

/**
 * Pure helpers backing `InfiniteDataGrid`'s persistent-by-id selection mode.
 *
 * The grid renders one page of a server-filtered list at a time, so its own
 * `@tanstack/react-table` selection state is keyed by *visible-row index* and
 * can only ever describe what's on screen. When the parent owns the selection
 * as a set of ids (assignment editors), the visible subset must never be
 * treated as the whole truth — otherwise searching narrows the rows and
 * silently drops (and, on save, unassigns) everything that didn't match. These
 * helpers keep the authoritative id set intact across search/sort/pagination.
 */

export type RowKeyFn<T> = (row: T) => string;
export type RowPredicate<T> = (row: T) => boolean;

/**
 * Build the index-keyed visual selection for the currently visible rows from
 * the authoritative id set. A row renders checked when its id is in the set OR
 * it is force-selected — a display-only overlay (e.g. an asset assigned through
 * a group while editing a person's direct assignments).
 */
export const deriveIdRowSelection = <T>(
  rows: T[],
  selectedIds: string[],
  getRowKey: RowKeyFn<T>,
  isForceSelected?: RowPredicate<T>
): RowSelectionState => {
  const set = new Set(selectedIds.map(String));
  const selection: RowSelectionState = {};
  rows.forEach((row, index) => {
    selection[index] = set.has(getRowKey(row)) || (isForceSelected?.(row) ?? false);
  });
  return selection;
};

/**
 * Reconcile a checkbox toggle against the persistent id set: replace only the
 * portion of the selection that is currently visible + editable, and preserve
 * every other selected id (filtered out, or on an unloaded page). Force-selected
 * rows are display-only and never enter the set.
 *
 * @param currentIds   the parent's authoritative selection before the toggle
 * @param rows         the currently visible rows
 * @param newSelection the index-keyed selection react-table produced from the toggle
 */
export const reconcileIdSelection = <T>(
  currentIds: string[],
  rows: T[],
  newSelection: RowSelectionState,
  getRowKey: RowKeyFn<T>,
  isForceSelected?: RowPredicate<T>
): string[] => {
  const isForced = (row: T) => isForceSelected?.(row) ?? false;
  const editableVisibleIds = new Set(
    rows.filter((row) => !isForced(row)).map(getRowKey)
  );
  const nowSelectedVisibleIds = rows
    .filter((row, index) => newSelection[index] && !isForced(row))
    .map(getRowKey);
  const preserved = currentIds
    .map(String)
    .filter((id) => !editableVisibleIds.has(id));
  return Array.from(new Set([...preserved, ...nowSelectedVisibleIds]));
};
